import { CreditCard, Award } from 'lucide-react';
import chinniRoboLogo from '../assets/chinni_robo.png';

type TabType = 'flashcards' | 'test';

interface FloatingTabBarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  flashcardCount: number;
  showFlashcards?: boolean;
  showTests?: boolean;
  showChatbot?: boolean;
  isChatbotOpen?: boolean;
  onChatbotToggle?: () => void;
}

export function FloatingTabBar({
  activeTab,
  onTabChange,
  flashcardCount,
  showFlashcards = true,
  showTests = true,
  showChatbot = false,
  isChatbotOpen = false,
  onChatbotToggle,
}: FloatingTabBarProps) {
  const hasContentTabs = showFlashcards && showTests;

  // Don't render if no tabs to show and no chatbot
  if (!hasContentTabs && !showChatbot) return null;

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40
                 ${!hasContentTabs && showChatbot ? 'w-auto' : showChatbot ? 'w-[calc(100%-2rem)] sm:w-[420px]' : 'w-[calc(100%-2rem)] sm:w-[320px]'}
                 bg-white/95 backdrop-blur-xl
                 rounded-full shadow-lg border border-gray-200/50
                 p-1.5 flex gap-1
                 safe-area-bottom`}
    >
      {showFlashcards && (
        <button
          onClick={() => onTabChange('flashcards')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 px-3 rounded-full text-sm font-semibold transition-all duration-200 ${
            activeTab === 'flashcards' && !isChatbotOpen
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <CreditCard className="w-4 h-4" />
          <span>Cards</span>
          <span className={`text-xs ${activeTab === 'flashcards' && !isChatbotOpen ? 'text-white/80' : 'text-gray-400'}`}>
            ({flashcardCount})
          </span>
        </button>
      )}

      {showTests && (
        <button
          onClick={() => onTabChange('test')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-3 px-3 rounded-full text-sm font-semibold transition-all duration-200 ${
            activeTab === 'test' && !isChatbotOpen
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Award className="w-4 h-4" />
          <span>Tests</span>
        </button>
      )}

      {showChatbot && hasContentTabs && (
        <div className="w-px bg-gray-200 my-2 flex-shrink-0" />
      )}

      {showChatbot && (
        <button
          onClick={onChatbotToggle}
          className={`${!hasContentTabs ? 'flex-1' : ''} flex items-center justify-center gap-2 py-2.5 px-4 rounded-full text-sm font-semibold transition-all duration-200 ${
            isChatbotOpen
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
              : !hasContentTabs
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md hover:from-amber-600 hover:to-orange-600'
                : 'bg-orange-50 text-coral-warm hover:bg-orange-100'
          }`}
        >
          <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
            <img src={chinniRoboLogo} alt="" className="w-full h-full object-cover" />
          </div>
          <span>{!hasContentTabs ? 'Ask Chinni' : 'Chinni'}</span>
        </button>
      )}
    </div>
  );
}
